import type { ProjectDocumentListItem } from "@shared/types";
import { AxiosError } from "axios";
import { Download, ExternalLink, FileText, History, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareAction } from "@/features/sharing/components/share-action";
import {
	Eyebrow,
	FloatPanel,
	Pill,
	StudioButton,
} from "@/features/studio/theme";
import {
	createProjectDocument,
	createProjectDocumentVersion,
	downloadProjectDocument,
	getProjectDocuments,
	openProjectDocument,
} from "../../services/project-documents-api.service";
import { DocumentUploadDialog } from "./document-upload-dialog";
import { DocumentVersionHistoryDialog } from "./document-version-history-dialog";

interface DocumentsTabProps {
	projectId: string;
}

function formatDateTime(value: string): string {
	return new Date(value).toLocaleString("en-IN");
}

export function DocumentsTab({
	projectId,
}: DocumentsTabProps): React.ReactElement {
	const [documents, setDocuments] = useState<ProjectDocumentListItem[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
	const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
		null,
	);
	const [historyOpen, setHistoryOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [permissionDenied, setPermissionDenied] = useState(false);

	const loadDocuments = useCallback(async () => {
		setIsLoading(true);
		setPermissionDenied(false);
		try {
			const response = await getProjectDocuments(projectId);
			setDocuments(response.documents);
		} catch (error) {
			const status =
				error instanceof AxiosError
					? error.response?.status
					: (error as { response?: { status?: number } })?.response?.status;
			if (status === 403) {
				setPermissionDenied(true);
				setDocuments([]);
				return;
			}
			toast.error("Failed to load project documents");
		} finally {
			setIsLoading(false);
		}
	}, [projectId]);

	useEffect(() => {
		void loadDocuments();
	}, [loadDocuments]);

	const handleCreateDocument = async (data: {
		title: string;
		type: "contract" | "invoice" | "permit";
		description?: string;
		file: File;
	}) => {
		setIsSubmitting(true);
		try {
			await createProjectDocument(projectId, data);
			toast.success("Project document uploaded");
			await loadDocuments();
		} catch {
			toast.error("Failed to upload project document");
			throw new Error("upload failed");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCreateVersion = async (documentId: string, file: File) => {
		await createProjectDocumentVersion(projectId, documentId, file);
		await loadDocuments();
	};

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div className="flex justify-between items-end flex-wrap gap-4">
					<div>
						<Eyebrow style={{ fontSize: 9 }}>DOCUMENTS · LOADING</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 34,
								lineHeight: 1.05,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "8px 0 0",
							}}
						>
							Assets &amp;{" "}
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									color: "var(--color-dashboard-accent-display)",
								}}
							>
								contracts
							</span>
							.
						</h2>
					</div>
				</div>
				<div className="space-y-3">
					<Skeleton className="h-16 w-full" />
					<Skeleton className="h-16 w-full" />
					<Skeleton className="h-16 w-full" />
				</div>
			</div>
		);
	}

	if (permissionDenied) {
		return (
			<div className="space-y-6">
				<div className="flex justify-between items-end flex-wrap gap-4">
					<div>
						<Eyebrow style={{ fontSize: 9 }}>ACCESS DENIED</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 34,
								lineHeight: 1.05,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "8px 0 0",
							}}
						>
							Assets &amp;{" "}
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									color: "var(--color-dashboard-accent-display)",
								}}
							>
								contracts
							</span>
							.
						</h2>
					</div>
				</div>
				<FloatPanel elevation={1} padding={24}>
					<p className="text-sm text-center text-[var(--color-dashboard-muted)]">
						You do not have permission to access project documents.
					</p>
				</FloatPanel>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-end flex-wrap gap-4">
				<div>
					<Eyebrow style={{ fontSize: 9 }}>
						DOCUMENTS · {documents.length} ITEMS
					</Eyebrow>
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 34,
							lineHeight: 1.05,
							letterSpacing: "-0.02em",
							color: "var(--color-dashboard-text)",
							margin: "8px 0 0",
						}}
					>
						Assets &amp;{" "}
						<span
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								color: "var(--color-dashboard-accent-display)",
							}}
						>
							contracts
						</span>
						.
					</h2>
				</div>
				<StudioButton
					variant="primary"
					size="md"
					icon={Plus}
					onClick={() => setIsUploadDialogOpen(true)}
				>
					Upload document
				</StudioButton>
			</div>

			{documents.length === 0 ? (
				<FloatPanel elevation={1} padding={32}>
					<div className="flex flex-col items-center gap-4 text-center">
						<FileText className="h-10 w-10 text-[var(--color-dashboard-muted)]/40" />
						<p className="text-sm text-[var(--color-dashboard-muted)] font-medium max-w-sm">
							No project documents yet
						</p>
					</div>
				</FloatPanel>
			) : (
				<div className="flex flex-col gap-4">
					{documents.map((document) => (
						<FloatPanel key={document.id} elevation={1} padding={22}>
							<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
								<div className="min-w-0">
									<div className="flex flex-wrap items-center gap-2">
										<p
											style={{
												fontFamily: "var(--font-display)",
												fontSize: 16.5,
												fontWeight: 600,
												color: "var(--color-dashboard-text)",
											}}
										>
											{document.title}
										</p>
										<Pill tone="neutral" dot={false}>
											{document.type}
										</Pill>
										<Pill tone="active" dot={false}>
											v{document.latestVersion.versionNumber}
										</Pill>
									</div>
									<p className="mt-2 text-xs text-[var(--color-dashboard-muted)]">
										Uploaded by{" "}
										<span className="font-semibold">
											{document.latestVersion.uploadedByName ?? "Unknown"}
										</span>{" "}
										on {formatDateTime(document.updatedAt)}
									</p>
									{document.description && (
										<p className="mt-2 text-sm text-[var(--color-dashboard-muted-dark)]">
											{document.description}
										</p>
									)}
								</div>
								<div className="flex flex-wrap gap-2">
									<ShareAction
										artifact={{
											type: "project_document",
											id: document.id,
											projectId,
										}}
										label="Share"
									/>
									<StudioButton
										variant="secondary"
										size="sm"
										icon={ExternalLink}
										onClick={() =>
											void openProjectDocument(
												document.latestVersion.downloadUrl,
											)
										}
									>
										Open latest
									</StudioButton>
									<StudioButton
										variant="secondary"
										size="sm"
										icon={Download}
										onClick={() =>
											void downloadProjectDocument(
												document.latestVersion.downloadUrl,
												document.latestVersion.originalFileName,
											)
										}
									>
										Download
									</StudioButton>
									<StudioButton
										variant="secondary"
										size="sm"
										icon={History}
										onClick={() => {
											setSelectedDocumentId(document.id);
											setHistoryOpen(true);
										}}
									>
										Version history
									</StudioButton>
								</div>
							</div>
						</FloatPanel>
					))}
				</div>
			)}

			<DocumentUploadDialog
				open={isUploadDialogOpen}
				onOpenChange={setIsUploadDialogOpen}
				onSubmit={handleCreateDocument}
				isSubmitting={isSubmitting}
			/>

			<DocumentVersionHistoryDialog
				projectId={projectId}
				documentId={selectedDocumentId}
				open={historyOpen}
				onOpenChange={setHistoryOpen}
				onVersionUpload={handleCreateVersion}
			/>
		</div>
	);
}
